import { CalendarDays, X } from "lucide-react";
import { fmtDate, fmtTime } from "../../data/plannerDates";

// 06:00 … 23:30 in half-hour steps
const TIMES = Array.from({ length: 36 }, (_, i) => 6 + i * 0.5);

const field =
  "w-full font-body font-700 text-sm bg-white border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black transition-colors";

const TimeSelect = ({ value, onChange, testid, placeholder }) => (
  <select data-testid={testid} value={value == null ? "" : value} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={field}>
    <option value="">{placeholder}</option>
    {TIMES.map((t) => (
      <option key={t} value={t}>
        {fmtTime(t)}
      </option>
    ))}
  </select>
);

// Exact dates: arrival + departure with the time you're free from / must
// leave by. Optional — the month buttons still work without it.
export const TripDates = ({ value, onChange, trip }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const hasDates = !!(value.start && value.end);
  const summary = () => {
    if (!trip || trip.invalid) return null;
    const bits = [`${trip.planDays} full day${trip.planDays > 1 ? "s" : ""} to plan`];
    if (trip.arrivalEvening) bits.push(`an arrival evening (${fmtDate(trip.arrivalEvening)})`);
    if (trip.departureMorning) bits.push(`a departure morning (${fmtDate(trip.departureMorning)})`);
    if (trip.firstFrom != null) bits.push(`day 1 starts ${fmtTime(Math.max(10, trip.firstFrom))}`);
    if (trip.lastBy != null) bits.push(`last day wraps by ${fmtTime(trip.lastBy)}`);
    return bits.join(" · ");
  };

  return (
    <div data-testid="planner-dates" className="border border-zinc-200 rounded-xl p-4 md:p-5 bg-[#FBFAF7]">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="font-body font-800 text-xs tracking-[0.25em] flex items-center gap-2">
          <CalendarDays size={14} className="text-[#DF3226]" /> YOUR DATES
          <span className="font-700 tracking-normal text-black/40 normal-case">we&apos;ll dodge closing days and off-season stops</span>
        </p>
        {hasDates && (
          <button
            type="button"
            data-testid="planner-dates-clear"
            onClick={() => onChange({ start: null, end: null, arriveFrom: null, leaveBy: null })}
            className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/50 hover:text-[#DF3226] transition-colors"
          >
            <X size={12} /> Clear dates
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-zinc-200 rounded-xl p-3">
          <p className="font-display font-900 text-sm mb-2">Arriving</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block font-body font-700 text-[10px] tracking-widest text-black/45 mb-1">DATE</span>
              <input
                type="date"
                data-testid="planner-date-start"
                value={value.start || ""}
                onChange={(e) => {
                  const v = e.target.value || null;
                  // don't let an existing (earlier) end date cap how far out you
                  // can start — if the new start is past the end, clear the end
                  set({ start: v, ...(v && value.end && value.end < v ? { end: null } : {}) });
                }}
                className={field}
              />
            </label>
            <label className="block">
              <span className="block font-body font-700 text-[10px] tracking-widest text-black/45 mb-1">READY TO EXPLORE FROM</span>
              <TimeSelect testid="planner-time-arrive" value={value.arriveFrom} onChange={(v) => set({ arriveFrom: v })} placeholder="Not sure yet" />
            </label>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-3">
          <p className="font-display font-900 text-sm mb-2">Leaving</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block font-body font-700 text-[10px] tracking-widest text-black/45 mb-1">DATE</span>
              <input
                type="date"
                data-testid="planner-date-end"
                value={value.end || ""}
                min={value.start || undefined}
                onChange={(e) => set({ end: e.target.value || null })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="block font-body font-700 text-[10px] tracking-widest text-black/45 mb-1">NEED TO LEAVE BY</span>
              <TimeSelect testid="planner-time-leave" value={value.leaveBy} onChange={(v) => set({ leaveBy: v })} placeholder="Not sure yet" />
            </label>
          </div>
        </div>
      </div>
      {trip?.invalid && (
        <p data-testid="planner-dates-invalid" className="font-body font-700 text-xs text-[#DF3226] mt-3">
          {trip.invalid}
        </p>
      )}
      {trip && !trip.invalid && (
        <p data-testid="planner-dates-summary" className="font-body text-xs text-black/60 mt-3">
          {fmtDate(trip.start)} → {fmtDate(trip.end)}: <span className="font-700 text-black/80">{summary()}</span>
          {trip.tooLong && <span className="block font-700 text-[#DF3226] mt-1">Plans work best up to 12 sightseeing days — we&apos;ll plan the first 12.</span>}
        </p>
      )}
      {hasDates && !trip?.invalid && (
        <p className="font-body text-[11px] text-black/40 mt-1.5">
          Free only from late afternoon? That day becomes an arrival evening. Leaving before noon? That&apos;s a departure morning — neither counts as a sightseeing day.
        </p>
      )}
    </div>
  );
};
